import { Group, Progress, Stack, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { getPacingRatio } from "@/Utilities/pacing-helpers";
import classes from "./ImpressionsProgressCell.module.css";

const pacingClass = (pacing) => {
    if (pacing === null) return classes.empty;
    if (pacing >= 90) return classes.complete;
    if (pacing >= 80) return classes.high;
    if (pacing >= 70) return classes.medium;
    return classes.low;
};

export const ImpressionsProgressCell = ({
    delivered,
    booked,
    percentage,
    starttime,
    stoptime,
    smartAdsDelivered,
    smartAdsBooked,
    programmaticDelivered,
    programmaticBooked,
    customDelivered,
}) => {
    const { t } = useTranslation();

    if (!booked) {
        const deliveredCount = Number(delivered ?? 0);

        // AD-228: a manually served ad (Custom Tag) can run without a goal.
        // The bar, percentage and "/ goal" stay meaningless without a booking,
        // but the delivered count is real -- the same counter sum the
        // Performance tab shows -- so it is no longer hidden behind the
        // "No impressions" message. That message now means delivered 0.
        if (deliveredCount > 0) {
            return <Text fz={14} fw={600} className={classes.value} c="var(--value-fg)">{deliveredCount.toLocaleString()}</Text>;
        }

        return <Text fz={14} fw={400} className={classes.hint} c="var(--hint-fg)">{t('ads.noImpressions')}</Text>;
    }

    // Custom Delivery has no goal of its own, so its presence alone is enough
    // to show the breakdown — an ad delivered only by the publisher would
    // otherwise fall through to the manual-tag message.
    const hasBreakdown = Number(smartAdsBooked ?? 0) > 0
        || Number(programmaticBooked ?? 0) > 0
        || Number(customDelivered ?? 0) > 0;
    const pacing = getPacingRatio(Number(delivered ?? 0), Number(booked), starttime, stoptime);
    const progress = pacingClass(pacing);

    const tooltipLabel = hasBreakdown ? (
        <Stack gap={2}>
            <Text fz={12} c="white">{t('ads.directOO')} {Number(smartAdsDelivered ?? 0).toLocaleString()} / {Number(smartAdsBooked ?? 0).toLocaleString()}</Text>
            <Text fz={12} c="white">{t('ads.programmatic')} {Number(programmaticDelivered ?? 0).toLocaleString()} / {Number(programmaticBooked ?? 0).toLocaleString()}</Text>
            <Text fz={12} c="white">{t('ads.customDelivery')} {Number(customDelivered ?? 0).toLocaleString()}</Text>
        </Stack>
    ) : t('ads.manualTagNotice');

    return (
        <Stack gap={4}>
            <Group justify="space-between" wrap="nowrap">
                <Text fz={14} fw={600} className={classes.value} c="var(--value-fg)">{Number(delivered ?? 0).toLocaleString()}</Text>
                <Text fz={14} fw={500} className={classes.hint} c="var(--hint-fg)">/ {Number(booked).toLocaleString()}</Text>
            </Group>
            <Progress value={percentage} className={progress} color="var(--progress-fg)" bg="var(--progress-bg)" size="sm" radius="xl" />
            <Group gap={4} wrap="nowrap">
                <Text fz={12} fw={500} className={classes.hint} c="var(--hint-fg)">
                    {pacing === null ? t('ads.notStarted') : t('ads.percentDelivered', { percent: Math.round(percentage) })}
                </Text>
                <Tooltip label={tooltipLabel} multiline w={220}>
                    <IconInfoCircle size={13} className={classes.infoIcon} />
                </Tooltip>
            </Group>
        </Stack>
    );
};
